import { useRef, useState } from "react";

import { useModal } from "@newsela/angelou";
import { useTracker } from "@newsela/snitch";
import { useRouter } from "next/router";

import { useMutation } from "@tanstack/react-query";
import AdvisoryModal from "@/assignments/AssignmentEditor/AdvisoryModal";
import useAssignmentCreate from "@/assignments/useAssignmentCreate";
import useLazyAssignmentGet from "@/assignments/useLazyAssignmentGet";
import {
  handleHasUnsavedChanges,
  handleProvideConsent,
  handleSetAssignment,
  verifyAssigneeSelection,
} from "@/assignments/utils/assignmentActions";
import { getLiteContentWithEarliestExpireDate } from "@/assignments/utils/assignmentUtils";
import { formatAssignmentForReducer } from "@/assignments/utils/formatAssignmentForReducer";
import { formatAssignmentPayload } from "@/assignments/utils/formatAssignmentPayload";
import { usePublishAssignment } from "@/assignments/utils/usePublishAssignment/usePublishAssignment";
import globalToasterRef from "@/base/globalToasterRef";
import { getLicenseTypeFromList } from "@/onboarding/onboarding.utils";
import { BannerMode } from "@/types/BannerMode.interface";
import ArticleLevel from "@/types/models/ArticleLevel.interface";
import { Assignment } from "@/types/models/Assignment.interface";
import { ContentLevel } from "@/types/models/ContentLevel.interface";
import { User } from "@/types/models/User.interface";
import { UserProperties } from "@/types/models/UserProperties.interface";
import useEventRecordMutation from "@/users/useEventRecordMutation/useEventRecordMutation";
import useGetUserFeatures from "@/users/v3/useUserFeaturesQuery";
import ActionsToolbar from "@/view/ActionsToolbar/";
import AssignModal from "@/view/AssignModal";
import contentTypes from "@/view/constants/contentTypes";
import quizOmissionReasons from "@/view/constants/quizOmissionReasons";
import {
  ASSIGNMENT_MODE,
  currentAssignmentMode,
} from "@/view/utils/currentAssignmentMode";
import handleSaveConfirmation from "@/view/utils/handleSaveConfirmation";
import updateUrlAfterSave from "@/view/utils/updateUrlAfterSave";
import useAssignmentReducerContext, {
  AssignModalProps,
} from "@/view/utils/useAssignmentReducerContext";
import usePollContext from "@/view/utils/usePollContext";
import { TCurrentLevel } from "@/view/utils/useUpdateLevel/useUpdateLevel";
import api, { ApiCatalog } from "@/api";
import buildAssignedGraphicOrganizerPayload from "@/view/utils/buildAssignedGraphicOrganizerPayload/buildAssignedGraphicOrganizerPayload";
import { useExportablesContext } from "@/components/AIComponents/ExportablesModal/ExportablesContextProvider/useExportablesContext";
import { HandleSave, ISavingState } from "./AssignActionsContainer.interface";

export const FIRST_ASSIGNMENT_COMPLETED_EVENT =
  "user_first_assignment_completed";

interface AssignActionsProps {
  bannerMode: BannerMode;
  content: {
    content_type: keyof typeof contentTypes;
    data: {
      article_levels: ArticleLevel[];
      quiz_omission_reason: keyof typeof quizOmissionReasons | null;
    };
    id: string;
    is_lite: boolean;
  };
  currentAssignment: Assignment;
  currentLevel: ContentLevel;
  isLiteContent?: boolean;
  mobileActionsToolbarDropdownRef: React.Ref<HTMLButtonElement> | null;
  originalAssignment: Assignment;
  setCurrentLevel: (level: TCurrentLevel) => void;
  toggleVocabularySupport: (a: boolean) => void;
  user: Pick<User, "first_name" | "last_name" | "unique_id">;
  userProperties: UserProperties;
}

const AssignActionsContainer = ({
  bannerMode,
  content,
  currentAssignment,
  currentLevel,
  isLiteContent = false,
  setCurrentLevel,
  toggleVocabularySupport,
  user,
  userProperties,
}: AssignActionsProps) => {
  const {
    dispatch,
    updatedAssignment,
    assignModal: {
      assignModalProps,
      assignModalTriggerRef,
      setIsAssignModalActive,
    },
  } = useAssignmentReducerContext() as {
    assignModal: AssignModalProps;
    dispatch: () => void;
    updatedAssignment: Assignment;
  };
  const router = useRouter();

  const { data: userFeatures } = useGetUserFeatures();
  const {
    assignedTemplates,
    formData,
    isAssignableGraphicOrganizersActivityChecked,
  } = useExportablesContext();
  const { postEventRecord } = useEventRecordMutation();
  const { mutateAsync: postGraphicOrganizers } = useMutation({
    mutationFn: async (payload: any) => {
      await api().post(ApiCatalog.graphicOrganizer.endpoint, payload);
    },
  });

  const initialSavingState: ISavingState = {
    type: null,
    value: false,
  };
  const [isOpeningAssignmentSettings, setIsOpeningAssignmentSettings] =
    useState(false);
  const [isSaving, setIsSaving] = useState(initialSavingState);

  const [advisoryModalProps, setAdvisoryModalActive] = useModal(false);
  const [shareModalProps, setShareModalActive] = useModal(false);
  const { handleAssignmentPoll } = usePollContext();
  const { trackAction } = useTracker();

  const initialAssignmentRef = useRef<Assignment | null>(null);
  const handleSetInitialAssignmentRef = (updatedAssignment: Assignment) => {
    initialAssignmentRef.current = updatedAssignment;
  };

  const [createAssignment] = useAssignmentCreate();
  const [fetchAssignment, { data: selectedAssignmentData }] =
    useLazyAssignmentGet();
  // TODO: remove this when useLazyAssignment is converted to ts
  const selectedAssignment = selectedAssignmentData as {
    assignment_id: number;
  };

  const liteContent = getLiteContentWithEarliestExpireDate([content?.data]); // util checks for is_lite status of content
  const maxScheduleDate =
    userProperties?.isLiteUser && liteContent.date_lite_product_grant_expires;

  // TODO: refactor to return if isAssignableGraphicOrganizersActivityChecked is false and instead call DELETE to archive the graphic organizer activity for the assignment
  const postOrganizers = async (assignmentId: string) => {
    if (!(assignedTemplates?.length > 0)) {
      return;
    }

    const templatePayload = !isAssignableGraphicOrganizersActivityChecked
      ? []
      : assignedTemplates;

    try {
      const payload = buildAssignedGraphicOrganizerPayload({
        templates: templatePayload,
        formData,
        updatedAssignmentId: assignmentId,
        userId: user?.unique_id,
        contentId: content.id,
      });
      await postGraphicOrganizers(payload);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSave = async ({
    eventProperties,
    isDraft,
    saveErrorToastProps,
    saveSuccessToastProps,
  }: HandleSave) => {
    setIsSaving({ type: isDraft ? "draft" : "publish", value: true });
    // Don't create non-draft assignments past the maxScheduleDate
    if (
      !isDraft &&
      Boolean(maxScheduleDate) &&
      (updatedAssignment.date_goes_live !== null) > maxScheduleDate
    ) {
      setIsSaving(initialSavingState);
      return;
    }

    const formattedAssignment = formatAssignmentPayload(
      updatedAssignment,
      isDraft,
      selectedAssignment,
    );

    const response = await createAssignment(formattedAssignment);

    if (response.error) {
      setIsSaving(initialSavingState);
      return globalToasterRef.current.displayToast(saveErrorToastProps);
    }

    if (response.data?.content_id) {
      await postOrganizers(response.data.content_id);
    }

    setIsSaving(initialSavingState);
    setIsAssignModalActive(false);
    const assignmentForReducer = formatAssignmentForReducer(
      response.data,
      false,
    );
    handleSetAssignment(assignmentForReducer, dispatch);
    updateUrlAfterSave({ response, router, selectedAssignment });
    // update the reference assignment for form value changes
    handleSetInitialAssignmentRef(updatedAssignment);
    handleHasUnsavedChanges(false, dispatch);
    handleSaveConfirmation({
      saveSuccessToastProps,
      isDraft,
      currentAssignment: updatedAssignment,
      setShareModalActive,
    });
    // We only want to track this event if save is successful
    trackAction(eventProperties);
    // A poll can be created, updated, or deleted on assignment save.
    handleAssignmentPoll({ assignmentId: response.data?.content_id });
    fetchAssignment({ id: response?.data?.content_id });
    // Add record to confirm the user's first assignment is complete.
    if (userFeatures?.includes("needs_first_assignment")) {
      const licenseType = getLicenseTypeFromList(userFeatures);
      postEventRecord({
        event_name: FIRST_ASSIGNMENT_COMPLETED_EVENT,
        data: {
          license_type: licenseType,
        },
      });
    }
    return response;
  };

  const headerId = currentLevel?.header_id;

  const { handlePublish } = usePublishAssignment({
    currentContentLevelId: headerId,
    handleProvideConsent,
    handleSave,
    isDisabled: false,
    setAdvisoryModalActive,
    updatedAssignment,
    verifyAssigneeSelection: () =>
      verifyAssigneeSelection(updatedAssignment, dispatch),
    maxScheduleDate,
  });

  const assignmentMode = currentAssignmentMode(updatedAssignment);
  const isUpdateMode = assignmentMode === ASSIGNMENT_MODE.UPDATE;

  const updateContentSettingsOverride = (isSelected = false) => {
    handleProvideConsent(isSelected ? headerId : undefined, dispatch);
  };

  const handleAdvisoryConfirmation = () => {
    setAdvisoryModalActive(false);
    if (isUpdateMode) {
      isOpeningAssignmentSettings
        ? setIsAssignModalActive(true)
        : handlePublish();
    } else {
      setIsAssignModalActive(true);
    }
    setIsOpeningAssignmentSettings(false);
  };

  return (
    <>
      <ActionsToolbar
        assignModalTriggerRef={assignModalTriggerRef}
        bannerMode={bannerMode}
        content={content}
        currentAssignment={currentAssignment}
        currentLevel={currentLevel}
        handlePublish={handlePublish}
        isLiteContent={isLiteContent}
        isSaving={isSaving}
        originalAssignment={currentAssignment}
        setAdvisoryModalActive={setAdvisoryModalActive}
        setCurrentLevel={setCurrentLevel}
        setIsAssignModalActive={setIsAssignModalActive}
        setIsOpeningAssignmentSettings={setIsOpeningAssignmentSettings}
        userProperties={userProperties}
      />
      <AssignModal
        assignModalProps={assignModalProps}
        content={content}
        currentAssignment={currentAssignment}
        currentLevel={currentLevel}
        handlePublish={handlePublish}
        handleSave={handleSave}
        setCurrentLevel={setCurrentLevel}
        setIsAssignModalActive={setIsAssignModalActive}
        shareModalProps={shareModalProps}
        toggleVocabularySupport={toggleVocabularySupport}
        userProperties={userProperties}
      />
      {/* @ts-expect-error Properties do not exist on type */}
      <AdvisoryModal
        advisoryModalProps={advisoryModalProps}
        assignmentMembers={updatedAssignment?.assignment_members}
        handleAdvisoryConfirmation={() => handleAdvisoryConfirmation()}
        setAdvisoryModalActive={setAdvisoryModalActive}
        updateContentSettingsOverride={updateContentSettingsOverride}
      />
    </>
  );
};

export default AssignActionsContainer;
